import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MeetingType, ClinicUserApiResponse } from '../../types/api';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

interface NewMeetingModalProps {
    isOpen: boolean;
    onClose: () => void;
    patient: any;
    users: ClinicUserApiResponse[];
    meetingTypes: MeetingType[];
    /** The logged-in user. Only a secretariat may choose an owner other than the
     *  patient's assigned therapist, so a missing value restricts rather than frees. */
    currentUser?: { type?: string } | null;
}

/** The patient's assigned therapist for a meeting type, as an id, or 0 when there is none.
 *  Call sites pass different patient shapes: the enriched rows carry `default_*` (assigned,
 *  else preferred), while task and ad-hoc rows carry only the raw column. Read both. */
const getAssignedTherapistId = (patient: any, userType?: string): number => {
    if (!patient) return 0;
    const raw = userType === 'doctor'
        ? (patient.default_doctor_id ?? patient.doctor_id)
        : (patient.default_case_manager_id ?? patient.case_manager_id);
    return Number(raw) || 0;
};

export const NewMeetingModal: React.FC<NewMeetingModalProps> = ({
    isOpen,
    onClose,
    patient,
    users,
    meetingTypes,
    currentUser
}) => {
    const [selectedOwner, setSelectedOwner] = useState<number | ''>('');
    const [selectedMeetingType, setSelectedMeetingType] = useState<number | ''>('');
    const [warningMessage, setWarningMessage] = useState<string>('');

    // The meeting-type list can arrive empty: the app loads it once at start-up together
    // with five other lists in a single Promise.all, so one failing call leaves every
    // screen with nothing and no retry. Fetch it here when that has happened, rather than
    // showing a popup whose only dropdown cannot be opened.
    const token = localStorage.getItem('token') || '';
    const hasMeetingTypes = !!meetingTypes && meetingTypes.length > 0;
    const meetingTypesFallback = useQuery({
        queryKey: ['meetingTypes', false],
        queryFn: () => apiService.getMeetingTypes(token, false),
        enabled: isOpen && !hasMeetingTypes && !!token,
        staleTime: 1000 * 60 * 5,
    });
    const availableMeetingTypes: MeetingType[] = useMemo(
        () => (hasMeetingTypes ? meetingTypes : (meetingTypesFallback.data || [])),
        [hasMeetingTypes, meetingTypes, meetingTypesFallback.data]
    );

    // Debug log
    useEffect(() => {
        if (isOpen) {
            console.log('[NewMeetingModal] Modal opened with meetingTypes:', availableMeetingTypes.length,
                hasMeetingTypes ? '(from props)' : '(fetched by the modal - the app-level list was empty)');
        }
    }, [isOpen, availableMeetingTypes, hasMeetingTypes]);

    // Everyone except a secretariat is held to the patient's assigned therapist.
    const isRestricted = currentUser?.type !== 'secretariat';

    const selectedType = useMemo(
        () => availableMeetingTypes.find(mt => mt.id === selectedMeetingType),
        [availableMeetingTypes, selectedMeetingType]
    );

    // The patient's therapist for this meeting type - assigned, else preferred - but only
    // when they are actually selectable here: active, and of the type this meeting needs.
    // An assignment pointing at someone inactive resolves to nobody so the booking falls to
    // the pool, which is the same rule the backend applies when resolving a Yoman resource.
    // Everyone uses this value: it is what a secretariat sees preselected and what everyone
    // else is locked to, so the two can never disagree, and it is always one of the options
    // below - a preselected id that is missing from the list renders as a blank select while
    // still being submitted.
    const defaultTherapistId = useMemo(() => {
        if (!selectedType) return 0;
        const id = getAssignedTherapistId(patient, selectedType.user_type);
        if (!id) return 0;
        return users.some(u => u.id === id && u.active && u.type === selectedType.user_type) ? id : 0;
    }, [selectedType, patient, users]);

    const lockedTherapistId = isRestricted ? defaultTherapistId : 0;

    // What is actually submitted. Derived rather than held in state, so a restricted user
    // cannot end up booking a stale owner left over from a previous meeting type.
    const effectiveOwner: number | '' = isRestricted ? (lockedTherapistId || '') : selectedOwner;

    // A restricted user on a patient with no assigned therapist: allowed, books to the pool.
    // Gated on the resolved type, not the raw id: until the meeting type is known there is
    // no role to look a therapist up by, so claiming "no assigned therapist" would be a
    // guess - and it reads as one when the type dropdown is still empty.
    const isPoolBooking = isRestricted && !!selectedType && !lockedTherapistId;

    // Set the default meeting type when the modal opens.
    // Keyed on the patient's id and the default itself rather than the patient object:
    // three call sites build `patient` as an inline object literal, so its identity changes
    // on every render. Depending on it re-ran this effect continuously and stamped the
    // meeting type back to its default, which made the dropdown impossible to change.
    const patientId = patient?.id;
    const defaultMeetingTypeId = patient?.next_meeting_type_id || '';
    useEffect(() => {
        if (isOpen) {
            setSelectedMeetingType(defaultMeetingTypeId);
        }
    }, [isOpen, patientId, defaultMeetingTypeId]);

    // Preselect the patient's therapist, and re-run on every meeting type change so
    // switching between a psychiatric and a case-manager meeting moves the owner to the
    // therapist for that role instead of leaving the previous one selected.
    useEffect(() => {
        setSelectedOwner(defaultTherapistId || '');
    }, [selectedMeetingType, defaultTherapistId]);

    // Filter users based on selected meeting type
    const filteredUsers = useMemo(() => {
        if (!selectedMeetingType) {
            return users.filter(u => u.active);
        }

        if (!selectedType) {
            return users.filter(u => u.active);
        }

        return users.filter(u => u.active && u.type === selectedType.user_type);
    }, [selectedMeetingType, selectedType, users]);

    // The options a restricted user may pick from: their patient's therapist, and nothing else.
    const ownerOptions = useMemo(() => {
        if (!isRestricted) return filteredUsers;
        return filteredUsers.filter(u => u.id === lockedTherapistId);
    }, [isRestricted, filteredUsers, lockedTherapistId]);

    // Update the warning when the meeting type changes
    useEffect(() => {
        if (selectedMeetingType && patient) {
            // Set warning message based on meeting type
            if (patient.set_meeting_warnings && patient.set_meeting_warnings[selectedMeetingType - 1]) {
                setWarningMessage(patient.set_meeting_warnings[selectedMeetingType - 1]);
            } else {
                setWarningMessage('');
            }
        } else {
            setWarningMessage('');
        }
    }, [selectedMeetingType, patient]);

    if (!isOpen) return null;

    const handleOpenYoman = () => {
        if (!selectedMeetingType) {
            alert('Please select a Meeting Type');
            return;
        }

        const token = localStorage.getItem('token') || '';
        const patientId = patient.id;
        const hmId = patient.HM_ID || '';

        let resourceId = '';
        if (effectiveOwner) {
            const selectedUser = users.find(u => u.id === effectiveOwner);
            resourceId = selectedUser?.yoman_id || '';
        }

        const yomanUrl = `${API_URL}/yoman_invite?token=${token}&patientId=${patientId}&HM_ID=${hmId}&meetingtype_id=${selectedMeetingType}&resourceId=${resourceId}`;

        window.open(yomanUrl, '_blank', 'noopener,noreferrer');
        onClose();
    };

    return (
        <ModalWrapper modalId="NewMeetingModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Set Meeting"
            width="!max-w-xl"
        >
            {/* Patient Info */}
            <div className="text-center text-sm text-[#366A75] mb-6">
                <strong>{patient?.HM_ID || 'N/A'}</strong> - {patient?.first_name || ''} {patient?.last_name || ''}
            </div>

            {/* Meeting Type */}
            <div className="flex items-center gap-4 mb-4">
                <label className="text-sm font-medium text-[#366A75] w-32 text-left">
                    Meeting Type
                </label>
                <div className="flex-1 relative">
                    <select
                        value={selectedMeetingType}
                        onChange={(e) => setSelectedMeetingType(Number(e.target.value) || '')}
                        className="flex-1 w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75] pr-10"
                    >
                        <option value="">Select an option</option>
                        {availableMeetingTypes.length > 0 ? (
                            (() => {
                                const activeTypes = availableMeetingTypes.filter(mt => mt.active);
                                const typesToShow = activeTypes.length > 0 ? activeTypes : availableMeetingTypes;
                                return typesToShow.map((mt) => (
                                    <option key={mt.id} value={mt.id}>
                                        {mt.name}
                                    </option>
                                ));
                            })()
                        ) : (
                            <option disabled>
                                {meetingTypesFallback.isLoading ? 'Loading meeting types...' : 'No meeting types available'}
                            </option>
                        )}
                    </select>
                    {/* Dropdown arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Owner */}
            <div className="mb-6">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-[#366A75] w-32 text-left">
                        Owner
                    </label>
                    <div className="flex-1 relative">
                        <select
                            value={effectiveOwner}
                            onChange={(e) => setSelectedOwner(Number(e.target.value) || '')}
                            className="flex-1 w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75] pr-10 disabled:opacity-50"
                            disabled={!selectedMeetingType || isRestricted}
                        >
                            <option value="">
                                {isPoolBooking ? 'Clinic pool' : 'Select an option (optional)'}
                            </option>
                            {ownerOptions.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </select>
                        {/* Dropdown arrow */}
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-4 h-4 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>
                {/* Pool notice */}
                {isPoolBooking && (
                    <div className="mt-2 text-sm text-[#366A75]">
                        This patient has no assigned therapist for this meeting type. The meeting will be offered to the clinic pool.
                    </div>
                )}
                {/* Warning Message */}
                {warningMessage && (
                    <div className="mt-3 text-base text-red-600 font-bold">
                        {warningMessage}
                    </div>
                )}
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-3">
                <button
                    onClick={handleOpenYoman}
                    className={POPUP_STYLES.button}
                >
                    Open Yoman
                </button>
                <button
                    onClick={onClose}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
